import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import s from './AddReview.module.scss';
import ProfileMenuHeader from 'modules/app/components/ProfileMenu/ProfileMenuHeader';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import TextField from 'components/shared/TextField';
import TextArea from 'components/shared/TextArea';
import { ReactComponent as GreyStar } from 'images/new-icons/grey-star-big.svg';
import { ReactComponent as Star } from 'images/new-icons/yellow-star-big.svg';
import { ReactComponent as IconUploadBg } from 'images/new-icons/upload-image.svg';
import Button from 'components/shared/Button';
import useAddReview from 'sdk/hooks/salon/useAddReview';
import useIsAuth from 'sdk/hooks/useIsAuth';
import { useGetMyUser } from 'sdk/hooks/user/useGetMyUser';
import { useTranslation } from 'react-i18next';

const AddReview = () => {
    const navigate = useNavigate();
    const { id } = useParams()
    const [searchParams] = useSearchParams();
    const serviceId = searchParams.get('serviceId');
    const specialistId = searchParams.get('specialistId');
    const { t } = useTranslation() as any;
    const [hoveredRating, setHoveredRating] = useState(0);
    const [hoveredServiceRating, setHoveredServiceRating] = useState(0);
    const [hoveredSpecialistRating, setHoveredSpecialistRating] = useState(0);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { data: isAuthData } = useIsAuth();

    const { data: userData } = useGetMyUser(isAuthData?.user?.id || '');

    const [text, setText] = useState<string>('')
    const [rank, setRank] = useState<number>(1)
    const [serviceRank, setServiceRank] = useState<number>(1)
    const [specialistRank, setSpecialistRank] = useState<number>(1)
    const [mediaUrls, setMediaUrls] = useState<string[]>([]);
    const [isAnon, setIsAnon] = useState<boolean>(false)

    const [isMediaLoading, setIsMediaLoading] = useState(false)

    const addReview = useAddReview(text, rank, id, mediaUrls, userData?.username, isAnon, serviceRank, specialistRank, serviceId || undefined, specialistId || undefined);


    const handleMediaClick = () => {
        if (fileInputRef.current && !isMediaLoading) {
            fileInputRef.current.click();
        }
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>, starIndex: number) => {
        setHoveredRating(starIndex);
    };

    const handleServiceMouseMove = (e: React.MouseEvent<HTMLDivElement>, starIndex: number) => {
        setHoveredServiceRating(starIndex);
    };

    const handleSpecialistMouseMove = (e: React.MouseEvent<HTMLDivElement>, starIndex: number) => {
        setHoveredSpecialistRating(starIndex);
    };

    const renderStars = () => {
        const stars: JSX.Element[] = [];
        for (let i = 1; i <= 5; i++) {
            const currentRating = hoveredRating || rank;
            stars.push(
                <div
                    key={i}
                    onMouseMove={(e) => handleMouseMove(e, i)}
                    onMouseEnter={() => setHoveredRating(i)}
                    onMouseLeave={() => {
                        setHoveredRating(0);
                    }}
                    onClick={() => setRank(i)}
                    className={s.star}
                >
                    {currentRating >= i ? (
                        <Star />
                    ) : (
                        <GreyStar />
                    )}
                </div>
            );
        }
        return stars;
    };

    const renderServiceStars = () => {
        const stars: JSX.Element[] = [];
        for (let i = 1; i <= 5; i++) {
            const currentRating = hoveredServiceRating || serviceRank;
            stars.push(
                <div
                    key={i}
                    onMouseMove={(e) => handleServiceMouseMove(e, i)}
                    onMouseEnter={() => setHoveredServiceRating(i)}
                    onMouseLeave={() => {
                        setHoveredServiceRating(0);
                    }}
                    onClick={() => setServiceRank(i)}
                    className={s.star}
                >
                    {currentRating >= i ? (
                        <Star />
                    ) : (
                        <GreyStar />
                    )}
                </div>
            );
        }
        return stars;
    };

    const renderSpecialistStars = () => {
        const stars: JSX.Element[] = [];
        for (let i = 1; i <= 5; i++) {
            const currentRating = hoveredSpecialistRating || specialistRank;
            stars.push(
                <div
                    key={i}
                    onMouseMove={(e) => handleSpecialistMouseMove(e, i)}
                    onMouseEnter={() => setHoveredSpecialistRating(i)}
                    onMouseLeave={() => {
                        setHoveredSpecialistRating(0);
                    }}
                    onClick={() => setSpecialistRank(i)}
                    className={s.star}
                >
                    {currentRating >= i ? (
                        <Star />
                    ) : (
                        <GreyStar />
                    )}
                </div>
            );
        }
        return stars;
    };

    const renderMedia = () => {
        if (isMediaLoading) {
            return <LoadingSpinner />;
        }

        return mediaUrls.map((url, index) => {
            if (url.endsWith('.mp4') || url.includes('video')) {
                return (
                    <video
                        key={index}
                        controls
                        playsInline
                        muted
                        className={s.uploadedVideo}
                    >
                        <source src={url} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                );
            } else {
                return (
                    <img
                        key={index}
                        src={url}
                        alt={`uploaded-${index}`}
                        className={s.uploadedImage}
                    />
                );
            }
        });
    };


    const uploadToCloudinary = async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', 'ml_default');

        const uploadEndpoint = file.type.startsWith('video')
            ? `${process.env.REACT_APP_CLOUDINARY_URL}/video/upload`
            : `${process.env.REACT_APP_CLOUDINARY_URL}/image/upload`;
        
        console.log('Upload endpoint:', uploadEndpoint);
        console.log('Upload preset: ml_default');
        console.log('File type:', file.type);
        
        try {
            const response = await fetch(uploadEndpoint, {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Upload successful:', data.secure_url);
                return data.secure_url;
            } else {
                const errorData = await response.json();
                console.error('Cloudinary error:', errorData);
                return null;
            }
        } catch (error) {
            console.error('Upload error:', error);
            return null;
        }
    };


    const uploadMediaFiles = async (files) => {
        const uploadPromises = files.map((file) => uploadToCloudinary(file));
        const urls = await Promise.all(uploadPromises);
        return urls.filter((url) => url !== null);
    };

    const onBgUpload = async (e) => {
        const files = e.target.files;
        console.log('Files selected:', files?.length);
        
        if (!files || files.length === 0) {
            console.log('No files selected');
            return;
        }
        
        setIsMediaLoading(true);
        try {
            console.log('Starting upload for', files.length, 'files');
            const uploadedUrls = await uploadMediaFiles(Array.from(files));
            console.log('Upload completed. URLs:', uploadedUrls);
            
            if (uploadedUrls.length > 0) {
                setMediaUrls((prevUrls) => [...prevUrls, ...uploadedUrls]);
                // Reset file input after successful upload
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            } else {
                console.warn('No URLs returned from upload');
            }
        } catch (err) {
            console.error('Upload error:', err);
        } finally {
            setIsMediaLoading(false);
        }
    };

    const LoadingSpinner = () => (
        <div className={s.spinner}>
            <div className={s.doubleBounce1}></div>
            <div className={s.doubleBounce2}></div>
        </div>
    );

    useEffect(() => {
        if (addReview.isSuccess) {
            navigate(`/salon-details/${serviceId}`)
        }
    }, [addReview.isSuccess])

    return (
        <div className={s.review}>
            <ProfileMenuHeader
                text="Add review"
                onClose={() => navigate(-1)}
            />
            <div className={s.review_form}>
                <div className={s.review_form_field}>
                    <div className={s.review_form_field_label}>{t('Review')}</div>
                    <TextArea
                        value={text ?? ""}
                        placeholder={t('Enter review')}
                        onChange={(e) => setText(e.target?.value)}
                    />
                </div>
                <div className={s.review_form_field}>
                    <div className={s.review_form_field_label}>{t('Salon rating')}</div>
                    <div className={s.review_form_field_rating}>
                        {renderStars()}
                    </div>
                </div>
                <div className={s.review_form_field}>
                    <div className={s.review_form_field_label}>{t('Service rating')}</div>
                    <div className={s.review_form_field_rating}>
                        {renderServiceStars()}
                    </div>
                </div>
                <div className={s.review_form_field}>
                    <div className={s.review_form_field_label}>{t('Specialist rating')}</div>
                    <div className={s.review_form_field_rating}>
                        {renderSpecialistStars()}
                    </div>
                </div>
                <div className={s.review_form_field}>
                    <div className={s.review_form_field_label}>{t('Add photos / videos')}</div>
                    <div className={s.medias} onClick={handleMediaClick}>
                        <form id="bgForm" className={s.bgUploadLabel}>
                            <label htmlFor="bgUpload">
                                <input
                                    type="file"
                                    name="file"
                                    id="bgUpload"
                                    className={s.bgUploadInput}
                                    onChange={onBgUpload}
                                    accept={'image/*, video/*'}
                                    multiple
                                    ref={fileInputRef}
                                />
                                <IconUploadBg />
                            </label>
                        </form>
                        <div className={s.review_media}>
                            {renderMedia()}
                        </div>
                    </div>
                </div>
                <div className={s.review_form_anon}>
                    <input
                        type='checkbox'
                        onChange={(e: any) => setIsAnon(e.target.checked)}
                    />
                    <div>{t('Leave A Review Anonymously')}</div>
                </div>
            </div>
            <Button
                wide
                darkGreen
                className={s.button}
                loading={isMediaLoading}
                disabled={isMediaLoading}
                onClick={() => {
                    addReview.mutate()

                }}
            >
                {t('Post your review')}
            </Button>
        </div>
    );
};

export default AddReview;
